/**
 * Story Loader - FOUC Prevention & Autoplay Gate
 *
 * Purpose:
 * - FOUC Prevention: Hide story until JS ready
 * - User Gesture Gate: Enable autoplay after user interaction
 * - Progress Indicator: Show loading progress (important on mobile)
 * - Mobile-First: Fast on 3G, smooth on 4G/WiFi
 *
 * Lifecycle:
 * 1. Page Load → Loader visible, Story hidden
 * 2. Assets Loading → Progress updates
 * 3. Assets Ready → Show "Start" button
 * 4. User Clicks → Capture gesture, start story
 * 5. Story Starts → Fade out loader, fade in story
 *
 * Integration:
 * - Coordinates with StoryMediaManager (user gesture flag)
 * - Coordinates with StoryCore (initialization timing)
 * - Mobile-First (no image loading wait on mobile)
 *
 * File: assets/js/story-loader.js
 */

(function(window) {
  'use strict';

  /* ----------------------------------------------------------
     1. Story Loader Class
     ---------------------------------------------------------- */

  class StoryLoader {
    constructor() {
      // DOM Elements
      this.loaderEl = null;
      this.progressBar = null;
      this.progressText = null;
      this.startButton = null;
      this.storyContainer = null;

      // State
      this.assetsReady = false;
      this.userGestureReceived = false;
      this.storyStarted = false;

      // Progress tracking
      this.currentProgress = 0;

      // Mobile detection
      this.isMobile = this.detectMobile();

      // Timeout handle (force start after 10s)
      this.timeoutHandle = null;

      // Initialize
      this.init();
    }

    /* ----------------------------------------------------------
       Initialization
       ---------------------------------------------------------- */

    init() {
      // Wait for DOM
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      // Get DOM elements
      this.loaderEl = document.getElementById('storyLoader');
      this.progressBar = document.getElementById('loaderProgress');
      this.progressText = document.getElementById('loaderText');
      this.startButton = document.getElementById('loaderStart');
      if (this.startButton && this.isEN()) this.startButton.setAttribute('aria-label', 'Start the story');
      if (this.isEN()) { var lt = document.getElementById('loaderText'); if (lt && /Story wird geladen/.test(lt.textContent)) lt.textContent = 'Loading the story...'; }
      this.storyContainer = document.querySelector('.story-container');

      // Check if loader exists (might not on all templates)
      if (!this.loaderEl) {
        if (window.location.search.includes('debug=story')) {
          console.warn('[StoryLoader] Loader element not found - skipping');
        }
        return;
      }

      if (window.location.search.includes('debug=story')) {
        console.log('[StoryLoader] Initialized - Mobile:', this.isMobile);
      }

      // Setup event listeners
      this.setupEventListeners();

      // Start monitoring asset loading
      this.startMonitoring();

      // Fallback: Force start after 10s if assets don't load
      this.timeoutHandle = setTimeout(() => {
        if (!this.assetsReady) {
          console.warn('[StoryLoader] Timeout reached - Force starting story');
          this.forceStart();
        }
      }, 10000); // 10 seconds
    }

    setupEventListeners() {
      // Start button click
      if (this.startButton) {
        this.startButton.addEventListener('click', () => this.onStartClicked());
      }

      // Document ready state changes
      document.addEventListener('readystatechange', () => this.onReadyStateChange());
    }

    /* ----------------------------------------------------------
       Story Meta Override (via Post Code Injection)
       ---------------------------------------------------------- */

    applyStoryMeta() {
      var titleEl = document.getElementById('loaderTitle');
      var subtitleEl = document.getElementById('loaderSubtitle');
      if (!titleEl) return;

      // Source 1: Post data from hidden DOM element (default)
      var postData = document.getElementById('storyPostData');
      var title = postData ? postData.dataset.title : '';
      var subtitle = postData ? postData.dataset.subtitle : '';

      // Source 2: window.storyMeta override (via Post Code Injection)
      var meta = window.storyMeta;
      if (meta) {
        if (meta.title) title = meta.title;
        if (meta.subtitle) subtitle = meta.subtitle;
      }

      // Apply title (plain text)
      if (title) titleEl.textContent = title;

      // Apply subtitle (support \n → <br> for line breaks from Code Injection)
      if (subtitle && subtitleEl) {
        // Escape HTML first, then convert newlines to <br>
        var safe = subtitle.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        subtitleEl.innerHTML = safe.replace(/\n/g, '<br>');
      }

      if (window.location.search.includes('debug=story')) {
        console.log('[StoryLoader] Title/subtitle applied:', { title: title, subtitle: subtitle, source: meta ? 'storyMeta override' : 'post data' });
      }
    }

    /* ----------------------------------------------------------
       Mobile Detection
       ---------------------------------------------------------- */

    detectMobile() {
      return (
        window.innerWidth < 768 ||
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      );
    }

    /* ----------------------------------------------------------
       Asset Loading Monitoring
       ---------------------------------------------------------- */

    startMonitoring() {
      // Initial progress
      this.updateProgress(0, this.t('Lädt...', 'Loading...'));

      // Check current state
      this.onReadyStateChange();
    }

    onReadyStateChange() {
      const state = document.readyState;

      if (window.location.search.includes('debug=story')) {
        console.log('[StoryLoader] ReadyState:', state);
      }

      switch (state) {
        case 'loading':
          this.updateProgress(10, this.t('Lädt...', 'Loading...'));
          break;

        case 'interactive':
          // DOM ready, scripts loading
          this.updateProgress(50, this.t('Story wird vorbereitet...', 'Preparing the story...'));
          this.checkIfReady();
          break;

        case 'complete':
          // All resources loaded
          this.updateProgress(90, 'Fast fertig...');
          this.checkIfReady();
          break;
      }
    }

    checkIfReady() {
      // Check if StoryCore exists (main dependency)
      const storyCoreExists = typeof window.StoryCore !== 'undefined';

      // On mobile: Don't wait for images (faster)
      // On desktop: Can wait for images (optional)
      const imagesReady = this.isMobile ? true : this.checkImagesLoaded();

      if (window.location.search.includes('debug=story')) {
        console.log('[StoryLoader] Check ready:', {
          storyCore: storyCoreExists,
          images: imagesReady,
          readyState: document.readyState
        });
      }

      // Ready if: StoryCore exists AND (images ready OR mobile)
      if (storyCoreExists && imagesReady && document.readyState !== 'loading') {
        this.onAssetsReady();
      }
    }

    checkImagesLoaded() {
      // Simple check: Are main images loaded?
      // (For production, could use imagesLoaded library)
      const images = this.storyContainer?.querySelectorAll('img') || [];
      if (images.length === 0) return true; // No images = ready

      let loadedCount = 0;
      images.forEach(img => {
        if (img.complete && img.naturalHeight !== 0) {
          loadedCount++;
        }
      });

      return loadedCount === images.length;
    }

    /* ----------------------------------------------------------
       Assets Ready
       ---------------------------------------------------------- */

    onAssetsReady() {
      if (this.assetsReady) return; // Already called

      this.assetsReady = true;
      clearTimeout(this.timeoutHandle);

      if (window.location.search.includes('debug=story')) {
        console.log('[StoryLoader] Assets ready - Vorpuffer-Phase');
      }

      // Apply title/subtitle (storyMeta from post data or Code Injection)
      this.applyStoryMeta();

      // Pano-Warmstart (Davids Befund 2026-07-25: „Panorama muss nachladen"):
      // das Hub-Panorama JETZT in den HTTP-Cache holen — der Nutzer liest
      // derweil das Intro; Pannellum findet die Datei später im Cache.
      // Bewusst NICHT Teil des Lade-Balkens (kann groß sein, blockiert nie).
      this.warmPanorama();

      // Vorpuffer-Gate (Davids Befund 2026-07-25: „Video läuft nicht los"):
      // das Intro-Video in den Lade-Balken aufnehmen — der Start-Knopf
      // erscheint erst, wenn genug gepuffert ist (oder der Timeout fällt).
      this.prebufferIntroVideo(() => {
        this.updateProgress(100, this.t('Bereit!', 'Ready!'));
        setTimeout(() => {
          this.showStartButton();
        }, 300);
      });
    }

    /* ----------------------------------------------------------
       Vorpuffern: Intro-Video + Panorama (2026-07-25)
       ---------------------------------------------------------- */

    warmPanorama() {
      if (!window.fetch) return;
      var slide = document.querySelector('.pano-slide');
      if (!slide) return;
      var mobil = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
      var src = (mobil && slide.dataset.panoSrcMobile) || slide.dataset.panoSrc;
      if (!src) return;
      fetch(src, { credentials: 'same-origin' }).catch(function () { /* Warmstart ist Komfort */ });
      if (window.location.search.includes('debug=story')) {
        console.log('[StoryLoader] Pano-Warmstart:', src.split('/').pop());
      }
    }

    prebufferIntroVideo(done) {
      // Erstes Szenen-Video der Tour (sg-Intro bzw. erstes Story-Video)
      var vid = document.querySelector('.sg-scrolly__media video, .story-panel video');
      if (!vid) { done(); return; }

      // Live-Befund 2026-08-19 (Nadelöhr: erstes Video startete erst beim Zurückkommen):
      // vid.load() SETZT das Element zurück — hatte ein Story-Modul (nb-/sg-Block 1) das
      // Intro-Video unter dem Loader bereits gestartet, war es danach pausiert, und das
      // Modul (Gewinner unverändert) startete es nicht erneut. Deshalb: load() nur, wenn
      // noch keine Daten da sind, und einen vorher laufenden Zustand nach dem Puffern
      // wiederherstellen (nur was wir gestört haben — keine Modul-Logik hier).
      var warAmLaufen = false;
      try {
        warAmLaufen = !vid.paused && !vid.ended;
        vid.preload = 'auto';
        if (vid.readyState < 3) vid.load();
      } catch (e) { done(); return; }
      var fertigMelden = function () {
        if (warAmLaufen && vid.paused && !vid.ended) {
          try { var p = vid.play(); if (p && p.catch) p.catch(function () {}); } catch (e2) {}
        }
        done();
      };

      var ZIEL_SEK = 8;        // so viel Puffer trägt über den Einstieg
      var TIMEOUT_MS = 25000;  // dünne Leitung: irgendwann trotzdem rein
      var t0 = Date.now();
      var self = this;

      var iv = setInterval(function () {
        var sek = 0;
        try { if (vid.buffered.length) sek = vid.buffered.end(vid.buffered.length - 1); } catch (e) {}
        var ziel = (vid.duration && isFinite(vid.duration))
          ? Math.min(vid.duration, ZIEL_SEK) : ZIEL_SEK;
        var anteil = Math.max(0, Math.min(1, sek / ziel));
        // Balken-Segment 90→99% gehört dem Video-Puffer
        self.updateProgress(90 + Math.round(anteil * 9), self.t('Video wird geladen…', 'Loading video…'));

        var fertig = anteil >= 1 || vid.readyState >= 4;
        var timeout = Date.now() - t0 > TIMEOUT_MS;
        if (fertig || timeout) {
          clearInterval(iv);
          if (timeout && window.location.search.includes('debug=story')) {
            console.log('[StoryLoader] Video-Vorpuffer-Timeout — starte trotzdem');
          }
          fertigMelden();
        }
      }, 200);
    }

    /* ----------------------------------------------------------
       Show Start Button
       ---------------------------------------------------------- */

    showStartButton() {
      if (!this.startButton) return;

      // Hide progress bar and loading text
      if (this.progressBar) {
        this.progressBar.parentElement.style.display = 'none';
      }
      if (this.progressText) {
        this.progressText.style.display = 'none';
      }

      // Show button
      this.startButton.hidden = false;
      this.startButton.classList.add('is-visible');

      // Focus button for keyboard users
      setTimeout(() => {
        this.startButton.focus();
      }, 100);
    }

    /* ----------------------------------------------------------
       User Clicked Start
       ---------------------------------------------------------- */

    onStartClicked() {
      if (this.storyStarted) return; // Already started

      if (window.location.search.includes('debug=story')) {
        console.log('[StoryLoader] Start button clicked - Capturing user gesture');
      }

      // Mark as started
      this.storyStarted = true;
      this.userGestureReceived = true;

      // Unlock audio via MediaManager (must happen during user gesture!)
      if (window.StoryMediaManager) {
        window.StoryMediaManager.enableAutoplay();

        if (window.location.search.includes('debug=story')) {
          console.log('[StoryLoader] Audio unlocked via MediaManager');
        }
      }

      // Request iOS Orientation Permission ONLY if story contains 360° panoramas
      // Must be called during user gesture - perfect timing here!
      if (this.storyHasPanoramas()) {
        this.requestOrientationPermission();
      }

      // Disable button
      this.startButton.disabled = true;

      // Safari-Befund 2026-08-19 (Nadelöhr/Sperrgebiet, Live): Das Intro-Video wird
      // von den Story-Modulen schon UNTER dem Loader gestartet — der Wrapper ist da
      // noch visibility:hidden, und Safari lehnt play() für unsichtbare Videos ab
      // (Chrome nicht). Danach startet es niemand neu → Standbild bis zum Zurück-
      // kommen. Hier sind wir IN der Nutzer-Geste: einmal jetzt anspielen, und
      // sicherheitshalber noch einmal, sobald der Wrapper sichtbar ist (startStory).
      this.ensureIntroVideoPlaying('geste');

      // Start story
      this.startStory();
    }

    /* Intro-Video des ersten Panels anspielen, falls es (noch) steht — nur das
       Video des ERSTEN Panels, nie eines späteren. Stumm/Ton bleibt, wie das
       Modul es gesetzt hat (sg-Sound-Szenen haben in enableAutoplay schon
       entmutet). Scheitert play() (z. B. noch unsichtbar), versucht es der
       zweite Aufruf nach dem Einblenden. */
    /* Mini-i18n (Sichtung 2026-08-19): EN-Touren liegen unter /en/… — Loader-Texte
       (Fortschritt, Start-Knopf) englisch, sonst deutsch. Kein eigenes Sprachsystem. */
    isEN() {
      try { return /^\/en(\/|$)/.test(window.location.pathname) || document.documentElement.lang === 'en'; } catch (e) { return false; }
    }
    t(de, en) { return this.isEN() ? en : de; }

    ensureIntroVideoPlaying(grund) {
      var firstPanel = document.querySelector('.story-panel');
      var vid = firstPanel ? firstPanel.querySelector('video') : null;
      if (!vid) return;
      if (!vid.paused || vid.ended) {
        if (window.location.search.includes('debug=story')) console.log('[StoryLoader] Intro-Video läuft bereits (' + grund + ')');
        return;
      }
      try {
        vid.playsInline = true;
        var p = vid.play();
        if (p && p.then) p.then(function () {
          if (window.location.search.includes('debug=story')) console.log('[StoryLoader] Intro-Video gestartet (' + grund + ')');
        }).catch(function (err) {
          if (window.location.search.includes('debug=story')) console.log('[StoryLoader] Intro-Video play() abgelehnt (' + grund + '):', err && err.name);
        });
      } catch (e) { /* defensiv */ }
    }

    /* ----------------------------------------------------------
       iOS Orientation Permission (for 360° panoramas)
       ---------------------------------------------------------- */

    /**
     * Check if the story contains any 360° panorama panels
     * Only request orientation permission if panoramas exist
     */
    storyHasPanoramas() {
      const hasPano = document.querySelector('.pano-slide') !== null;

      if (window.location.search.includes('debug=story')) {
        console.log('[StoryLoader] Story has panoramas:', hasPano);
      }

      return hasPano;
    }

    requestOrientationPermission() {
      // Check if DeviceOrientationEvent.requestPermission exists (iOS 13+)
      if (typeof DeviceOrientationEvent === 'undefined') {
        // Not available - probably desktop
        window.StoryOrientationPermission = 'not-required';
        return;
      }

      if (typeof DeviceOrientationEvent.requestPermission !== 'function') {
        // Not iOS 13+ or already granted (Android, older iOS)
        window.StoryOrientationPermission = 'granted';
        return;
      }

      // iOS 13+ - Request permission
      DeviceOrientationEvent.requestPermission()
        .then(permissionState => {
          window.StoryOrientationPermission = permissionState;

          if (window.location.search.includes('debug=story')) {
            console.log('[StoryLoader] Orientation permission:', permissionState);
          }

          // Also request DeviceMotion permission if available (some iOS versions need both)
          if (typeof DeviceMotionEvent !== 'undefined' &&
              typeof DeviceMotionEvent.requestPermission === 'function') {
            DeviceMotionEvent.requestPermission()
              .then(motionState => {
                if (window.location.search.includes('debug=story')) {
                  console.log('[StoryLoader] Motion permission:', motionState);
                }
              })
              .catch(err => {
                console.warn('[StoryLoader] Motion permission denied:', err);
              });
          }
        })
        .catch(error => {
          console.warn('[StoryLoader] Orientation permission denied:', error);
          window.StoryOrientationPermission = 'denied';
        });
    }

    /* ----------------------------------------------------------
       Start Story
       ---------------------------------------------------------- */

    startStory() {
      if (window.location.search.includes('debug=story')) {
        console.log('[StoryLoader] Starting story...');
      }

      // 1. Fade out loader
      this.loaderEl.classList.add('is-hidden');

      // 2. Fade in story (after loader starts fading)
      setTimeout(() => {
        if (this.storyContainer) {
          this.storyContainer.style.opacity = '1';
          this.storyContainer.style.visibility = 'visible';
        }
      }, 200);

      // 3. Initialize StoryCore if not done
      setTimeout(() => {
        this.initializeStoryCore();
      }, 400);

      // 4. Notify ambient audio (and others) that story has started
      setTimeout(() => {
        window.dispatchEvent(new Event('story:started'));
      }, 600);

      // 4b. Safari-Befund 2026-08-19: Intro-Video ggf. jetzt (Wrapper sichtbar) anspielen
      setTimeout(() => {
        this.ensureIntroVideoPlaying('sichtbar');
      }, 700);

      // 5. Cleanup after animations complete
      setTimeout(() => {
        this.cleanup();
      }, 1000);
    }

    initializeStoryCore() {
      // Check if StoryCore already initialized by story-init.js
      // If yes, skip ENTIRE initialization (including modules)
      // This prevents double registration of modules
      if (window.storyCore) {
        if (window.location.search.includes('debug=story')) {
          console.log('[StoryLoader] StoryCore already initialized by story-init.js - skipping');
        }
        return;
      }

      // Find story container
      const container = document.querySelector('[data-story-template]');
      if (!container) {
        console.warn('[StoryLoader] Story container not found');
        return;
      }

      // Initialize StoryCore
      if (typeof window.StoryCore === 'undefined') {
        console.error('[StoryLoader] StoryCore not loaded!');
        return;
      }

      if (window.location.search.includes('debug=story')) {
        console.log('[StoryLoader] Initializing StoryCore (first time)...');
      }

      // Get template type and build selector string (StoryCore expects selector, not element)
      const templateType = container.dataset.storyTemplate;
      const selector = `[data-story-template="${templateType}"]`;
      window.storyCore = new window.StoryCore(selector);

      // Initialize modules (only if StoryCore was created by loader)
      if (window.StoryModuleVideoAutoplay) {
        new window.StoryModuleVideoAutoplay(window.storyCore);
      }

      if (window.StoryModulePanelReveal) {
        new window.StoryModulePanelReveal(window.storyCore);
      }

      if (window.StoryModuleActivePanel) {
        new window.StoryModuleActivePanel(window.storyCore);
      }

      if (window.StoryNavigation) {
        new window.StoryNavigation();
      }
    }

    /* ----------------------------------------------------------
       Force Start (Timeout fallback)
       ---------------------------------------------------------- */

    forceStart() {
      console.warn('[StoryLoader] Force starting due to timeout');

      // Mark as ready
      this.assetsReady = true;
      this.userGestureReceived = true; // Skip gate

      // Notify MediaManager
      if (window.StoryMediaManager) {
        window.StoryMediaManager.userGestureReceived = true;
      }

      // Start immediately
      this.startStory();
    }

    /* ----------------------------------------------------------
       Update Progress
       ---------------------------------------------------------- */

    updateProgress(percent, text) {
      this.currentProgress = percent;

      // Update progress bar
      if (this.progressBar) {
        this.progressBar.style.width = percent + '%';
        this.progressBar.setAttribute('aria-valuenow', percent);
      }

      // Update text
      if (this.progressText && text) {
        this.progressText.textContent = text;
      }

      if (window.location.search.includes('debug=story')) {
        console.log('[StoryLoader] Progress:', percent + '%', '-', text);
      }
    }

    /* ----------------------------------------------------------
       Cleanup
       ---------------------------------------------------------- */

    cleanup() {
      // Remove loader from DOM (free memory)
      if (this.loaderEl && this.loaderEl.parentNode) {
        this.loaderEl.parentNode.removeChild(this.loaderEl);
      }

      if (window.location.search.includes('debug=story')) {
        console.log('[StoryLoader] Cleanup complete');
      }
    }
  }

  /* ----------------------------------------------------------
     2. Initialize Story Loader
     ---------------------------------------------------------- */

  // Create instance (auto-initializes)
  const storyLoader = new StoryLoader();

  // Expose for debugging
  window.StoryLoader = storyLoader;

})(window);
